import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { User } from '../../entities/user/user';
import { ApolloService } from '../../services/apollo.service';
import { UsersService } from '../../services/users/users.service';
import * as fromCatalog from '../notification-settings-list/graphql';
import * as fromGraphQl from '../notification-diagnostics/graphql';
import { DELIVERY_CHANNEL_VALUES, deliveryChannelLabelKey } from '../notification-diagnostics/notification-labels';

/** Anzeigezustand einer Kanal-Zelle in der Abo-Einsicht. */
export type ChannelSubscriptionState = 'on' | 'off' | 'hidden';

/**
 * Detail einer Notification samt aller ihrer Zustellungen (#737).
 *
 * Die Eltern-Ebene: eine Notification mit ihren Kind-Zustellungen. Aufgerufen mit der
 * NOTIFICATION-ID — aus dem Nachrichten-Tab ueber row.id, aus dem Zustell-Tab ueber
 * row.notificationId.
 *
 * `getNotificationAdminDetail` liefert null, wenn die Notification durch die Aufbewahrungsfrist
 * bereits geloescht ist, waehrend man noch auf einen aelteren Listenstand schaut. Das ist ein
 * legitimes Ergebnis und bekommt einen eigenen Zustand — keine Fehlermeldung.
 */
@Component({
  selector: 'app-notification-admin-detail',
  templateUrl: './notification-admin-detail.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NotificationAdminDetailComponent implements OnInit {

  private readonly apolloService = inject(ApolloService);
  private readonly usersService = inject(UsersService);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  readonly detail = signal<fromGraphQl.NotificationAdminDetail | null>(null);
  readonly loading = signal(false);
  readonly error = signal(false);
  /** Geladen, aber null — nicht dasselbe wie „noch nicht geladen". */
  readonly notFound = signal(false);

  readonly catalog = signal<fromCatalog.NotificationTypeInfo[]>([]);
  readonly catalogLoading = signal(false);
  readonly catalogError = signal(false);

  private readonly users = signal<User[]>([]);

  readonly channels = DELIVERY_CHANNEL_VALUES;

  /**
   * Nutzerliste und Detail treffen in beliebiger Reihenfolge ein — als computed spielt das keine
   * Rolle. Solange die Liste fehlt, bleibt die UUID stehen; fuer Support brauchbar.
   */
  readonly recipientDisplay = computed(() => {
    const detail = this.detail();
    if (!detail) {
      return '';
    }
    const match = this.users().find((user) => user.id === detail.recipientUserId);
    return match?.displayName ?? detail.recipientUserId;
  });

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (!id) {
      this.notFound.set(true);
      return;
    }
    this.loadUsers();
    this.loadDetail(id);
  }

  channelLabelKey(channel: string): string {
    return deliveryChannelLabelKey(channel);
  }

  /**
   * HIDDEN heisst: der Kanal ist fuer diesen Typ gar nicht vorgesehen — das ist etwas anderes als
   * „abgeschaltet" und darf nicht als Kreuz erscheinen, sonst liest Support eine Abmeldung, wo keine
   * ist. Ein fehlender Kanal wird genauso behandelt.
   */
  channelState(type: fromCatalog.NotificationTypeInfo, channel: string): ChannelSubscriptionState {
    const info = type.channels?.find((c) => c.channel === channel);
    if (!info || info.policy === fromCatalog.ChannelPolicy.HIDDEN) {
      return 'hidden';
    }
    return info.effectiveEnabled ? 'on' : 'off';
  }

  private loadDetail(id: string): void {
    this.loading.set(true);
    this.error.set(false);
    this.notFound.set(false);

    // network-only: der Default von apollo.query ist cache-first, und dieser Screen wird typisch
    // direkt nach einem Requeue erneut geoeffnet.
    this.apolloService
      .query<fromGraphQl.AdminDetailResponse>(
        fromGraphQl.NOTIFICATION_ADMIN_DETAIL_QUERY,
        { id },
        { fetchPolicy: 'network-only' }
      )
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          const detail = result?.data?.getNotificationAdminDetail ?? null;
          this.detail.set(detail);
          this.notFound.set(!detail);
          this.loading.set(false);
          if (detail) {
            this.loadCatalogForUser(detail.recipientUserId);
          }
        },
        error: () => {
          this.loading.set(false);
          this.error.set(true);
        }
      });
  }

  /**
   * Abo-Einsicht: derselbe Katalog wie in den eigenen Einstellungen, aber fuer einen fremden Nutzer
   * und ausschliesslich lesend. Es gibt keine Mutation, die Praeferenzen fuer Fremde setzt, und das
   * ist Absicht.
   */
  private loadCatalogForUser(userId: string): void {
    this.catalogLoading.set(true);
    this.catalogError.set(false);

    this.apolloService
      .query<fromCatalog.CatalogQueryResponse>(fromGraphQl.NOTIFICATION_CATALOG_FOR_USER_QUERY, {
        userId,
        locale: this.translate.currentLang ?? this.translate.getDefaultLang() ?? 'de'
      })
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.catalog.set(result?.data?.catalog ?? []);
          this.catalogLoading.set(false);
        },
        error: () => {
          this.catalogLoading.set(false);
          this.catalogError.set(true);
        }
      });
  }

  /** Nur zum Aufloesen der Empfaenger-UUID. Faellt sie aus, bleibt die UUID stehen. */
  private loadUsers(): void {
    this.usersService
      .getUsers()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (users) => this.users.set(users ?? []),
        error: () => this.users.set([])
      });
  }
}
